import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DollarSignIcon, BuildingIcon, CalendarIcon, FileTextIcon, CheckCircleIcon } from 'lucide-react';
export function BillFinancingWizard() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    loanPackage: '',
    financingAmount: '',
    requestedTerm: '30',
    purpose: '',
    additionalNotes: ''
  });
  // Mock bill data
  const bill = {
    id: id,
    billNumber: 'BILL-2024-002',
    supplierName: 'Spice Traders Co',
    amount: 87500,
    dueDate: '2024-02-20'
  };
  const dfccLoanPackages = [{
    id: 'standard',
    name: 'Standard Financing',
    interestRate: '12%',
    maxTerm: '90 days',
    processingTime: '48 hours',
    description: 'Ideal for regular business operations with flexible terms'
  }, {
    id: 'premium',
    name: 'Premium Financing',
    interestRate: '10.5%',
    maxTerm: '120 days',
    processingTime: '24 hours',
    description: 'Lower rates for established businesses with priority processing'
  }, {
    id: 'express',
    name: 'Express Financing',
    interestRate: '14%',
    maxTerm: '60 days',
    processingTime: '12 hours',
    description: 'Quick approval for urgent cash flow needs'
  }, {
    id: 'sme',
    name: 'SME Growth Package',
    interestRate: '11%',
    maxTerm: '90 days',
    processingTime: '36 hours',
    description: 'Special rates for small and medium enterprises'
  }];
  const handleSubmit = () => {
    console.log('Submitting financing request:', {
      billId: id,
      ...formData
    });
    navigate('/bills');
  };
  const selectedPackage = dfccLoanPackages.find(p => p.id === formData.loanPackage);
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-4142">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-4143">
        <button onClick={() => navigate(`/bill-detail/${id}`)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-4144">
          <ArrowLeftIcon size={20} data-id="element-4145" />
          Back to Bill
        </button>
      </div>
      {/* Progress Steps */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4146">
        <div className="flex items-center justify-between mb-8" data-id="element-4147">
          <div className="flex-1" data-id="element-4148">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-4149">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-4150">
                1
              </div>
              <div className="ml-3" data-id="element-4151">
                <div className="text-sm font-medium" data-id="element-4152">Bill Details</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-4153"></div>
          <div className="flex-1" data-id="element-4154">
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-4155">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-4156">
                2
              </div>
              <div className="ml-3" data-id="element-4157">
                <div className="text-sm font-medium" data-id="element-4158">Select Provider</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-4159"></div>
          <div className="flex-1" data-id="element-4160">
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-4161">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-4162">
                3
              </div>
              <div className="ml-3" data-id="element-4163">
                <div className="text-sm font-medium" data-id="element-4164">Financing Terms</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 4 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-4165"></div>
          <div className="flex-1" data-id="element-4166">
            <div className={`flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-4167">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-4168">
                4
              </div>
              <div className="ml-3" data-id="element-4169">
                <div className="text-sm font-medium" data-id="element-4170">Review & Submit</div>
              </div>
            </div>
          </div>
        </div>
        {/* Step 1: Bill Details */}
        {step === 1 && <div className="space-y-6" data-id="element-4171">
            <div data-id="element-4172">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4173">
                Bill Information
              </h2>
              <p className="text-gray-600" data-id="element-4174">
                Review the bill details for financing
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-4175">
              <div className="grid grid-cols-2 gap-4" data-id="element-4176">
                <div data-id="element-4177">
                  <div className="text-sm text-gray-500" data-id="element-4178">Bill Number</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-4179">
                    {bill.billNumber}
                  </div>
                </div>
                <div data-id="element-4180">
                  <div className="text-sm text-gray-500" data-id="element-4181">Supplier</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-4182">
                    {bill.supplierName}
                  </div>
                </div>
                <div data-id="element-4183">
                  <div className="text-sm text-gray-500" data-id="element-4184">Due Date</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-4185">
                    {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div data-id="element-4186">
                  <div className="text-sm text-gray-500" data-id="element-4187">Bill Amount</div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-4188">
                    Rs. {bill.amount.toLocaleString('en-LK')}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-4189">
              <div className="flex items-start gap-3" data-id="element-4190">
                <DollarSignIcon size={20} className="text-blue-600 mt-0.5" data-id="element-4191" />
                <div className="text-sm text-blue-800" data-id="element-4192">
                  <div className="font-medium mb-1" data-id="element-4193">
                    Bill Financing Benefits
                  </div>
                  <ul className="list-disc list-inside space-y-1" data-id="element-4194">
                    <li data-id="element-4195">Improve cash flow by paying suppliers early</li>
                    <li data-id="element-4196">Maintain good supplier relationships</li>
                    <li data-id="element-4197">Take advantage of early payment discounts</li>
                    <li data-id="element-4198">Flexible repayment terms</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-4199">
              <button onClick={() => setStep(2)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-4200">
                Continue
              </button>
            </div>
          </div>}
        {/* Step 2: Select Provider */}
        {step === 2 && <div className="space-y-6" data-id="element-4201">
            <div data-id="element-4202">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4203">
                Select Maybank Financing Package
              </h2>
              <p className="text-gray-600" data-id="element-4204">
                Choose a financing package that best suits your needs
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-4205">
              <div className="flex items-start gap-3" data-id="element-4206">
                <BuildingIcon size={20} className="text-blue-600 mt-0.5" data-id="element-4207" />
                <div data-id="element-4208">
                  <div className="font-semibold text-blue-900 mb-1" data-id="element-4209">
                    Maybank - Your Trusted Financial Partner
                  </div>
                  <div className="text-sm text-blue-800" data-id="element-4210">
                    As a valued Maybank customer, you have access to exclusive
                    financing packages tailored for your business needs.
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4" data-id="element-4211">
              {dfccLoanPackages.map(pkg => <label key={pkg.id} className={`block p-6 border-2 rounded-lg cursor-pointer transition-all ${formData.loanPackage === pkg.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} data-id="element-4212">
                  <input type="radio" name="loanPackage" value={pkg.id} checked={formData.loanPackage === pkg.id} onChange={e => setFormData({
              ...formData,
              loanPackage: e.target.value
            })} className="sr-only" data-id="element-4213" />
                  <div className="flex items-start justify-between" data-id="element-4214">
                    <div className="flex-1" data-id="element-4215">
                      <div className="flex items-center gap-2 mb-2" data-id="element-4216">
                        <div className="text-lg font-semibold text-gray-900" data-id="element-4217">
                          {pkg.name}
                        </div>
                        {formData.loanPackage === pkg.id && <CheckCircleIcon size={20} className="text-blue-600" data-id="element-4218" />}
                      </div>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-4219">
                        {pkg.description}
                      </p>
                      <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-4220">
                        <div data-id="element-4221">
                          <div className="text-gray-500" data-id="element-4222">Interest Rate</div>
                          <div className="font-medium text-gray-900" data-id="element-4223">
                            {pkg.interestRate}
                          </div>
                        </div>
                        <div data-id="element-4224">
                          <div className="text-gray-500" data-id="element-4225">Max Term</div>
                          <div className="font-medium text-gray-900" data-id="element-4226">
                            {pkg.maxTerm}
                          </div>
                        </div>
                        <div data-id="element-4227">
                          <div className="text-gray-500" data-id="element-4228">Processing Time</div>
                          <div className="font-medium text-gray-900" data-id="element-4229">
                            {pkg.processingTime}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>)}
            </div>
            <div className="flex justify-between" data-id="element-4230">
              <button onClick={() => setStep(1)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4231">
                Back
              </button>
              <button onClick={() => setStep(3)} disabled={!formData.loanPackage} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4232">
                Continue
              </button>
            </div>
          </div>}
        {/* Step 3: Financing Terms */}
        {step === 3 && <div className="space-y-6" data-id="element-4233">
            <div data-id="element-4234">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4235">
                Financing Terms
              </h2>
              <p className="text-gray-600" data-id="element-4236">
                Specify the financing amount and terms
              </p>
            </div>
            <div className="space-y-6" data-id="element-4237">
              <div data-id="element-4238">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4239">
                  Financing Amount (LKR) *
                </label>
                <input type="number" value={formData.financingAmount} onChange={e => setFormData({
              ...formData,
              financingAmount: e.target.value
            })} placeholder="Enter amount" max={bill.amount} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4240" />
                <div className="mt-1 text-sm text-gray-500" data-id="element-4241">
                  Maximum: Rs. {bill.amount.toLocaleString('en-LK')}
                </div>
              </div>
              <div data-id="element-4242">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4243">
                  Requested Term (Days) *
                </label>
                <select value={formData.requestedTerm} onChange={e => setFormData({
              ...formData,
              requestedTerm: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4244">
                  <option value="15" data-id="element-4245">15 days</option>
                  <option value="30" data-id="element-4246">30 days</option>
                  <option value="45" data-id="element-4247">45 days</option>
                  <option value="60" data-id="element-4248">60 days</option>
                  <option value="90" data-id="element-4249">90 days</option>
                </select>
              </div>
              <div data-id="element-4250">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4251">
                  Purpose of Financing *
                </label>
                <select value={formData.purpose} onChange={e => setFormData({
              ...formData,
              purpose: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4252">
                  <option value="" data-id="element-4253">Select purpose</option>
                  <option value="early_payment" data-id="element-4254">Early Payment Discount</option>
                  <option value="cash_flow" data-id="element-4255">Cash Flow Management</option>
                  <option value="supplier_relationship" data-id="element-4256">
                    Supplier Relationship
                  </option>
                  <option value="other" data-id="element-4257">Other</option>
                </select>
              </div>
              <div data-id="element-4258">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4259">
                  Additional Notes
                </label>
                <textarea value={formData.additionalNotes} onChange={e => setFormData({
              ...formData,
              additionalNotes: e.target.value
            })} rows={4} placeholder="Any additional information..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4260" />
              </div>
            </div>
            <div className="flex justify-between" data-id="element-4261">
              <button onClick={() => setStep(2)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4262">
                Back
              </button>
              <button onClick={() => setStep(4)} disabled={!formData.financingAmount || !formData.requestedTerm || !formData.purpose} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4263">
                Review Request
              </button>
            </div>
          </div>}
        {/* Step 4: Review & Submit */}
        {step === 4 && <div className="space-y-6" data-id="element-4264">
            <div data-id="element-4265">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4266">
                Review Financing Request
              </h2>
              <p className="text-gray-600" data-id="element-4267">
                Please review your financing request before submitting
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-6" data-id="element-4268">
              <div data-id="element-4269">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-4270">
                  BILL INFORMATION
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-4271">
                  <div data-id="element-4272">
                    <div className="text-sm text-gray-500" data-id="element-4273">Bill Number</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4274">
                      {bill.billNumber}
                    </div>
                  </div>
                  <div data-id="element-4275">
                    <div className="text-sm text-gray-500" data-id="element-4276">Supplier</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4277">
                      {bill.supplierName}
                    </div>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-4278">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-4279">
                  FINANCING DETAILS
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-4280">
                  <div data-id="element-4281">
                    <div className="text-sm text-gray-500" data-id="element-4282">Bank</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4283">
                      Maybank
                    </div>
                  </div>
                  <div data-id="element-4284">
                    <div className="text-sm text-gray-500" data-id="element-4285">Package</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4286">
                      {selectedPackage?.name}
                    </div>
                  </div>
                  <div data-id="element-4287">
                    <div className="text-sm text-gray-500" data-id="element-4288">Interest Rate</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4289">
                      {selectedPackage?.interestRate}
                    </div>
                  </div>
                  <div data-id="element-4290">
                    <div className="text-sm text-gray-500" data-id="element-4291">Processing Time</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4292">
                      {selectedPackage?.processingTime}
                    </div>
                  </div>
                  <div data-id="element-4293">
                    <div className="text-sm text-gray-500" data-id="element-4294">
                      Financing Amount
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4295">
                      Rs.{' '}
                      {parseFloat(formData.financingAmount).toLocaleString('en-LK')}
                    </div>
                  </div>
                  <div data-id="element-4296">
                    <div className="text-sm text-gray-500" data-id="element-4297">Term</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4298">
                      {formData.requestedTerm} days
                    </div>
                  </div>
                  <div data-id="element-4299">
                    <div className="text-sm text-gray-500" data-id="element-4300">Purpose</div>
                    <div className="text-base font-medium text-gray-900 capitalize" data-id="element-4301">
                      {formData.purpose.replace('_', ' ')}
                    </div>
                  </div>
                </div>
              </div>
              {formData.additionalNotes && <div className="pt-4 border-t border-gray-200" data-id="element-4302">
                  <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4303">
                    ADDITIONAL NOTES
                  </h3>
                  <p className="text-sm text-gray-700" data-id="element-4304">
                    {formData.additionalNotes}
                  </p>
                </div>}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-4305">
              <div className="flex items-start gap-3" data-id="element-4306">
                <FileTextIcon size={20} className="text-blue-600 mt-0.5" data-id="element-4307" />
                <div className="text-sm text-blue-800" data-id="element-4308">
                  By submitting this request, you agree to the financing terms
                  and conditions set by Maybank for the {selectedPackage?.name}.
                  The request will be processed within{' '}
                  {selectedPackage?.processingTime}.
                </div>
              </div>
            </div>
            <div className="flex justify-between" data-id="element-4309">
              <button onClick={() => setStep(3)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4310">
                Back
              </button>
              <button onClick={handleSubmit} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-4311">
                Submit Financing Request
              </button>
            </div>
          </div>}
      </div>
    </div>;
}